// The world the train runs through, one per city.
//
// `src/liveries.js` moved the TRAIN out of `TrainScene.vue` and into a table,
// because a Sydney board was drawing a Melbourne train. The world had the same
// bug and it was the louder one: a Sydney board was drawing a Melbourne train
// through a Melbourne boom-gate crossing, past a Melbourne sky-rail station, in
// front of a linear park that exists because the Victorian government took a
// level crossing away. None of that is a thing anybody in Canberra has seen.
//
// So the scenery moves here, the same way the paint did. One entry per city,
// each of them the flat shapes that city is, drawn in the scene's own idiom:
// the same ground line, the same rail, the same fixed train with the world
// sliding past it, the same four-second loop.
//
// ── A SIBLING FILE RATHER THAN MORE OF `liveries.js` ─────────────────────────
//
// The brief allowed either. This is its own file for two reasons. The first is
// that `liveries.js` is a table about VEHICLES and it says so at length — how a
// livery is drawn, why the markings are paths, what a pantograph means — and a
// harbour bridge is not a vehicle; filing it under the livery table would make
// that file's opening paragraph a lie. The second is the Melbourne guarantee:
// the `melbourne-metro` entry over there is a regression asset, held byte for
// byte, and the Melbourne backdrop over here is the same kind of promise about
// a different thing. Two promises, two files, two tests that can fail
// separately and tell you which one broke.
//
// IMPORT-FREE, exactly like `liveries.js` and `city.js`. The splash reads this
// on the first paint, before the store has answered anything, and nothing here
// may drag Vue or a network bundle in front of that. In particular it does not
// import `LIVERY_IDS` to check its own livery lists — `tests/backdrops.test.js`
// does that instead, which is the right place for a fact that has to be true at
// build time rather than at load time.
//
// ── HOW A WORLD IS DRAWN ─────────────────────────────────────────────────────
//
// The scene's units are the ones it has always used: a 220-unit window from
// y -24 to y 46, the rail at y 38, the train fixed at x 14 to 182. Scenery
// scrolls in an 800-unit pattern that is drawn twice, one spacing apart, so it
// wraps without a seam — the same 800 units the Melbourne scene has always
// used, because the loop's timing is built on it.
//
// An entry gives four lists of shapes and three facts:
//
//   wash    drawn once, behind everything, in WINDOW coordinates and NOT
//           scrolling: the water under a bridge, the haze on a horizon. A band
//           of water that scrolled would have a visible seam every four
//           seconds and water does not have seams.
//   far     behind the train, scrolling. Whatever is in the distance.
//   ground  in front of the sleepers and behind the train, scrolling: the
//           structure the train is standing on, and anything that has to cover
//           the track — a river, a park where the rails used to be.
//   near    in front of the train, scrolling. The things that pass between you
//           and the carriages, and the only layer that may hide the train.
//   lift    what the train does over the loop: `climb` up onto a viaduct and
//           down again (Melbourne, and only Melbourne), `rise` gently over a
//           river bridge (Brisbane), or `level`, which is no movement at all.
//           A bridge deck is level, and a train that bobbed on one would be
//           drawing a grade that is not there.
//   liveries  which trains belong to this city, in the order the splash parade
//           drives them through. The parade is grouped by city so the WORLD
//           changes six times per cycle rather than twelve.
//   name    the landmark, in words, for the caption and for anything that has
//           to say where this is.
//
// A shape is one of three things, kept deliberately small so the scene renders
// one loop rather than a switch: `{ d, fill }` a filled path, `{ d, stroke, sw }`
// a stroked one, `{ cx, cy, r, fill }` a circle. Rects and polygons are written
// as whichever is clearest and turned into paths by the helpers below, exactly
// as the livery table does with its markings.
//
// ── WHY MELBOURNE IS NOT IN THE LISTS ────────────────────────────────────────
//
// Melbourne's entry carries the facts — its liveries, its `climb`, its name —
// and four EMPTY shape lists, and that is not an omission. Its world is still
// drawn by hand in `TrainScene.vue`, in the markup it has always had, because
// it is the scene every person who has ever opened this app has been looking
// at: the crossing drawn in two layers, the ramp whose grade is one and a half
// degrees, the raked station shell. Re-expressing it as a list of paths would
// change the rendered markup, which is the one thing the brief said must not
// happen, and `tests/backdrops.test.js` holds that snapshot to the character.
// So the scene asks this table "is this Melbourne?" and, when it is, draws what
// it always drew.
//
// THE DESIGN VOCABULARY STILL HOLDS. The interface is achromatic; the trains
// are the one place with real colour and the scenery is the second, kept muted;
// `--la-signal` red is reserved for lateness and appears nowhere below. And
// there is no sky: every city's sky is the app's own dark ground, the way
// Melbourne's has always been, so that five different worlds still read as one
// app rather than five postcards.

/** A number, without the floating-point dust. `44.4 + 0.8` is not 45.199999…. */
const round = (v) => Math.round(v * 1000) / 1000;

/** A rect, as the path that draws it. */
const box = (x, y, w, h) => `M${round(x)} ${round(y)}H${round(x + w)}V${round(y + h)}H${round(x)}Z`;

/** A polygon's points, as the path that draws it. */
const poly = (points) => `M${points.trim().split(/\s+/).map((p) => p.replace(',', ' ')).join('L')}Z`;

/** A filled shape. */
const solid = (d, fill, opacity) => (opacity === undefined ? { d, fill } : { d, fill, opacity });

/** A drawn line: steelwork, a hanger, a mast. */
const drawn = (d, stroke, sw, opacity) => (
  opacity === undefined ? { d, stroke, sw } : { d, stroke, sw, opacity }
);

/** A circle: foliage, a lamp. */
const disc = (cx, cy, r, fill, opacity) => (
  opacity === undefined ? { cx, cy, r, fill } : { cx, cy, r, fill, opacity }
);

/** `n` copies of a shape, `step` apart, starting at `x0`. */
const repeat = (count, x0, step, make) => Array.from({ length: count }, (_, i) => make(x0 + i * step, i));

/** The scenery pattern's length. The scene's own number; changing it retimes the loop. */
export const SPACING = 800;
// ─────────────────────────────────────────────────────────────────────────────
// WHERE A LANDMARK HAS TO BE, IN THIS SCENE, TO BE SEEN AT ALL
//
// The train fills x 14 to 182 and y 10 to 36 of a 220-wide window, and it is
// opaque. So a world drawn between y 10 and y 38 is a world drawn behind the
// train, and the first pass at these four cities proved it: an Adelaide Oval
// whose roof sat at y 26 was an Adelaide Oval nobody ever saw.
//
// Melbourne's scene already solved this and the solution is its idiom rather
// than a trick: its landmarks are TALL. The crossbucks reach y -8, the signal
// hut's roof y 3, the station shell y -6. Everything stands on the same ground
// line and grows up past the carriage roofs, and the strips either side of the
// train — x 0 to 14 and x 182 to 220 — carry the rest as it arrives and leaves.
//
// Every landmark below is drawn to that rule: based near the ground line, with
// real mass above y 8.
// ─────────────────────────────────────────────────────────────────────────────

// ─────────────────────────────────────────────────────────────────────────────
// SYDNEY — the Harbour Bridge.
//
// The T1 North Shore trains really do cross it, and for the app's purposes so
// do the Metro and the light rail: a person in Sydney should see Sydney
// whichever line they are waiting on, and a Metro drawn at a boom gate in
// Bentleigh would be a worse lie than a Metro drawn on the bridge.
//
// THE SPAN IS 208 UNITS AND THE CROWN IS 56 ABOVE THE DECK, which is the real
// bridge's proportion — 503 metres by 134 — and it is also the only proportion
// that works here. The first attempt spanned the arch over 680 units so the
// train would be under it for most of the loop; at 220 units of window you then
// saw a third of it at a time, and a third of a very flat parabola is a
// horizontal line. Narrow it to just under the window's width and the whole
// arch passes once a lap, as an arch.
//
// Either side of it are the approach spans the real bridge has, on piers in the
// water, so the train is on a bridge for the whole loop and under the arch for
// part of it. The deck is LEVEL: the bridge is level, and that is the whole
// reason `lift` had to become a property of the world.
//
// THE BRIDGE HAS TWO SIDES AND THE TRAIN IS BETWEEN THEM. Drawn entirely behind
// the train it was a picture of an arch hanging in the sky over a train, which
// is not what anybody has ever seen: from a ferry you look THROUGH the near
// hangers at the carriages. So the arch is built twice — once in `far` and once,
// wider and lower and a shade darker, in `near` — and the train rides in the gap.
// Nothing on the near side is solid: two chord lines, an open web and hangers,
// every one of them a thin stroke, so the carriages read straight through it.
// The two stone pylons stay behind, because a pylon IS a solid mass and the one
// that used to stand in front took the whole train with it once a lap.
// ─────────────────────────────────────────────────────────────────────────────

const SYD_STEEL = '#6b7781';
const SYD_STEEL_DIM = '#515c66';
// The near side of the same steel, one shade down. It is the only depth cue a
// flat side elevation has: same shape, same paint, darker.
const SYD_STEEL_NEAR = '#4b555e';
const SYD_STEEL_NEAR_DIM = '#3a434b';
const SYD_STONE = '#8a8172';
const SYD_STONE_DIM = '#736b5e';
const SYD_DECK = 40;              // the underside of the track, where the deck is

const sydney = (() => {
  /**
   * One side of the arch: two chords, the web between them, the hangers under.
   *
   * Both sides of the bridge are the same drawing with different numbers, so it
   * is written once. The near side spans WIDER and springs LOWER, which is what
   * the nearer of two identical arches does in any honest elevation, and it is
   * drawn in the dimmer steel.
   */
  const side = ({ x0, x1, top, low, deck, steel, dim, sw, fade }) => {
    // A parabola through (x0, deck) and (x1, deck) with its crown at `apex`, as
    // the quadratic Bézier that draws it: a quadratic's midpoint is the average
    // of its ends and its control, so the control sits at 2·apex − deck.
    const chord = (apex) => `M${x0} ${deck} Q${(x0 + x1) / 2} ${2 * apex - deck} ${x1} ${deck}`;
    const height = (x, apex) => {
      const t = (x - x0) / (x1 - x0);
      const c = 2 * apex - deck;
      return round(((1 - t) ** 2) * deck + 2 * t * (1 - t) * c + (t ** 2) * deck);
    };

    // Sixteen panels, which on the far side is the 13-unit bay this bridge has
    // always been drawn with.
    const step = round((x1 - x0) / 16);
    const posts = repeat(15, x0 + step, step, (x) => round(x));

    // The truss: the verticals between the two chords and the zig-zag across
    // them, each as ONE path rather than thirty, so the browser rasterises two
    // things per copy instead of sixty.
    const web = [
      drawn(posts.map((x) => `M${x} ${height(x, top)}V${height(x, low)}`).join(''), dim, sw.post, fade),
      drawn(posts.map((x, i) => {
        const a = i % 2 ? top : low;
        const b = i % 2 ? low : top;
        return `M${x} ${height(x, a)}L${round(x + step)} ${height(x + step, b)}`;
      }).join(''), dim, sw.zig, fade),
    ];

    // Hangers, from the lower chord down to the deck, wherever the chord is
    // actually above it — near the springing it is not, and a hanger there would
    // be a line of length nothing.
    const hangers = drawn(
      posts
        .map((x) => ({ x, y: height(x, low) }))
        .filter((h) => h.y < deck - 6)
        .map((h) => `M${h.x} ${h.y}V${deck}`)
        .join(''),
      dim, sw.hanger, fade,
    );

    return [drawn(chord(top), steel, sw.top), drawn(chord(low), steel, sw.low), ...web, hangers];
  };

  const far = side({
    x0: 196, x1: 404, top: -18, low: -4, deck: SYD_DECK,
    steel: SYD_STEEL, dim: SYD_STEEL_DIM,
    sw: { top: 2.4, low: 1.6, post: 0.9, zig: 0.7, hanger: 0.6 },
  });

  // The near side: wider, lower, darker, and thinner in every member. Thinner
  // is not decoration — a 2.4-wide chord across the carriage windows is a bar
  // over the train, and the whole point of putting it in front is that you can
  // still see what is behind it.
  const near = side({
    x0: 190, x1: 410, top: -22, low: -6.4, deck: SYD_DECK + 1,
    steel: SYD_STEEL_NEAR, dim: SYD_STEEL_NEAR_DIM,
    sw: { top: 2, low: 1.3, post: 0.7, zig: 0.55, hanger: 0.5 },
    fade: 0.85,
  });

  // A pylon: the stone tower at the arch's foot, stepped the way the real ones
  // are, with the arched road opening through its base.
  const pylon = (x) => [
    solid(poly(`${x},44 ${x},0 ${x + 4},-6 ${x + 26},-6 ${x + 30},0 ${x + 30},44`), SYD_STONE),
    solid(box(x + 2, -9, 26, 3), SYD_STONE_DIM),
    solid(box(x, 8, 30, 2), SYD_STONE_DIM),
    solid(box(x, 21, 30, 2), SYD_STONE_DIM),
    solid(`M${x + 10} 40V26a5 5 0 0 1 10 0V40Z`, '#242a31'),
  ];

  // An approach span: a plate girder between two piers, with a pier under it.
  const approach = (x) => [
    solid(box(x, SYD_DECK, 84, 2.6), SYD_STEEL),
    solid(box(x, SYD_DECK + 2.2, 84, 0.8), SYD_STEEL_DIM),
    solid(poly(`${x + 78},42 ${x + 90},42 ${x + 88},46 ${x + 80},46`), '#5a636c'),
  ];

  return {
    wash: [
      // The harbour. Not scrolled: water has no seam to wrap.
      solid(box(0, 42.6, 220, 3.4), '#22404a'),
      solid(box(0, 42.6, 220, 0.5), '#31606d'),
    ],
    far: [
      // The far shore, a low headland the bridge lands on.
      solid('M470 42Q560 32 660 42Z', '#2b3a36'),
      // The Opera House, small, on the point beyond the bridge. Its sails
      // reach past the carriage roofs, because nothing below them is ever seen.
      solid(box(556, 36, 84, 6), '#8f959b'),
      solid('M562 36Q564 12 590 36Z', '#c2c7cc'),
      solid('M578 36Q581 4 610 36Z', '#ced3d8'),
      solid('M596 36Q600 14 622 36Z', '#c2c7cc'),
      solid('M612 36Q616 22 632 36Z', '#b2b8be'),
      // The far side of the arch: two chords, the truss between them, the
      // hangers under. The near side of it is in `near`, in front of the train.
      ...far,
      // Both pylons, because a pylon is stone and stone is opaque. The near one
      // stands at the near arch's foot and the train passes behind it once a
      // lap, exactly as it does behind the Melbourne station shell.
      ...pylon(404),
      ...pylon(160),
    ],
    ground: [
      ...approach(20),
      ...approach(104),
      solid(box(188, SYD_DECK, 224, 3), SYD_STEEL),
      solid(box(188, SYD_DECK + 2.4, 224, 0.9), SYD_STEEL_DIM),
      ...approach(412),
      ...approach(496),
    ],
    near: [
      // The near side of the arch, in front of the carriages: the train is
      // INSIDE the bridge, which is the one thing the far-only version could
      // never say. Every member here is a stroke — nothing solid, nothing
      // wider than a carriage window.
      ...near,
      // The deck's outer railing, passing in front of the wheels. Melbourne's
      // station deliberately has no edge screen; a bridge without a railing,
      // though, is a train balanced on a plank.
      solid(box(-20, 40.6, 840, 0.7), '#7e888f'),
      ...repeat(42, -14, 20, (x) => solid(box(x, 40.6, 0.7, 2.6), '#6b747c')),
    ],
  };
})();

// ─────────────────────────────────────────────────────────────────────────────
// BRISBANE — the Merivale bridge over the river.
//
// The only rail crossing of the Brisbane River, and every line out of Roma
// Street uses it. Behind it, the Story Bridge's two cantilever towers and the
// flat slabs of the city, which is the view from the Merivale deck.
//
// This is the one world besides Melbourne where the train moves: the approach
// really does climb to clear the river, and the rise is TWO units against
// Melbourne's four, on the same ramp positions, so one set of percentages in
// `TrainScene`'s keyframes serves both.
// ─────────────────────────────────────────────────────────────────────────────

const BNE_STEEL = '#63737d';
const BNE_DECK = '#6d7680';
// The near side of the Merivale bridge, a shade down from the deck it belongs
// to, the same way Sydney's near steel is a shade down from its far steel.
const BNE_NEAR = '#4d5760';
const BNE_NEAR_DIM = '#414b53';

const brisbane = (() => {
  // A Story Bridge tower: a tapering truss to a point, which is what the two
  // steel towers are from the river. High, because everything below the
  // carriage roofs is behind the train.
  const tower = (x) => [
    drawn(`M${x - 14} 10L${x} -20L${x + 14} 10`, BNE_STEEL, 1.6),
    drawn(`M${x - 9} 10L${x} -11L${x + 9} 10`, BNE_STEEL, 0.9),
    drawn(`M${x - 14} 10H${x + 14}`, BNE_STEEL, 1.4),
    drawn(`M${x} -20V10`, BNE_STEEL, 0.8),
  ];

  // The deck's own profile, as a function, so the near girder follows the ramps
  // rather than being a second set of numbers that drifts from the first: up
  // from the bank at 260, level across the river, down again from 598.
  const deckY = (x) => {
    if (x <= 260) return 40;
    if (x < 420) return round(40 - (3.4 * (x - 260)) / 160);
    if (x <= 598) return 36.6;
    if (x < 758) return round(36.6 + (3.4 * (x - 598)) / 160);
    return 40;
  };

  // The near girder rides 1.6 below the deck the train stands on, because the
  // nearer of two parallel things sits lower in an elevation, and its top chord
  // runs 15 above it. Between them the truss, open: a zig-zag and every other
  // vertical, all of them strokes, so the carriages read straight through the
  // web the way they do through Sydney's.
  //
  // FIFTEEN RATHER THAN FIVE. A truss that stopped under the skirt was a truss
  // nobody could tell was in front of anything — it sat where the bogies are
  // and read as more track. At 15 it crosses the lower windows, which is what
  // a through-truss does to a train inside it, and at 0.5 of a unit it crosses
  // them without hiding one.
  const NEAR_DROP = 1.6;
  const NEAR_RISE = 15;
  const gird = (x) => round(deckY(x) + NEAR_DROP);
  const rail = (x) => round(deckY(x) + NEAR_DROP - NEAR_RISE);
  const nodes = repeat(31, 240, 18, (x) => x).filter((x) => x <= 778);

  const nearTruss = [
    drawn(nodes.map((x) => `M${x} ${rail(x)}V${gird(x)}`).filter((_, i) => i % 2 === 0).join(''), BNE_NEAR_DIM, 0.55, 0.85),
    drawn(nodes.slice(0, -1).map((x, i) => {
      const a = i % 2 ? rail : gird;
      const b = i % 2 ? gird : rail;
      return `M${x} ${a(x)}L${x + 18} ${b(x + 18)}`;
    }).join(''), BNE_NEAR_DIM, 0.5, 0.8),
    drawn(nodes.map((x, i) => `${i ? 'L' : 'M'}${x} ${rail(x)}`).join(''), BNE_NEAR, 0.8),
  ];

  return {
    wash: [
      // The subtropical haze the city stands in. A band, not a sky: this scene
      // has never had a sky and is not getting one now.
      solid(box(0, -10, 220, 34), '#2c3a42', 0.5),
    ],
    far: [
      // The city, in flat slabs on the far bank. Every one of them tall enough
      // to be seen over the train, because none of them would be otherwise.
      ...[[40, -6, 15], [60, 4, 11], [76, -13, 17], [98, 1, 13], [116, -8, 19],
        [140, 6, 12], [157, -11, 16], [178, 0, 14], [197, 5, 10], [212, -5, 15],
        [232, 3, 13], [250, -14, 17], [272, 1, 11], [288, 6, 15], [308, -3, 13],
        [326, 4, 11], [342, -12, 16], [363, 2, 13], [381, 7, 12]]
        .map(([x, top, w], i) => solid(box(x, top, w, 26 - top), ['#3d4750', '#46525c', '#3f4f49'][i % 3])),
      solid(box(-20, 25, 840, 1.4), '#4e5a64'),
      // The Story Bridge: two towers, the arms between them, and the suspended
      // span in the middle.
      ...tower(486),
      ...tower(724),
      drawn('M404 20L486 10L605 16L724 10L800 20', BNE_STEEL, 1.4),
      drawn('M404 22H800', BNE_STEEL, 1.1),
      ...repeat(13, 500, 17, (x) => drawn(`M${x} 11.4V22`, BNE_STEEL, 0.6, 0.8)),
    ],
    ground: [
      // The river, covering the track the way Melbourne's linear park does.
      solid(box(236, 38, 546, 8), '#5a4a35'),
      solid(box(236, 38, 546, 0.7), '#6e5c42'),
      // The banks either end of the pattern, where the ramps come down.
      solid(box(-40, 38, 278, 8), '#4b463c'),
      solid(box(780, 38, 80, 8), '#4b463c'),
      // Piers, standing in the water.
      ...repeat(5, 300, 88, (x) => solid(poly(`${x - 5},39 ${x + 5},39 ${x + 3.5},46 ${x - 3.5},46`), '#5c646d')),
      // The deck: up from the bank at pattern 260, level across the river, down
      // again at 598. The same two ramps, at the same two places, as the
      // Melbourne viaduct — see the `rise` keyframes.
      solid(poly('260,40 420,36.6 598,36.6 758,40 758,42.4 598,39 420,39 260,42.4'), BNE_DECK),
      solid(poly('260,40 420,36.6 598,36.6 758,40 758,41 598,37.6 420,37.6 260,41'), '#828b95'),
    ],
    near: [
      // The Merivale bridge's NEAR girder and truss, in front of the train:
      // from a CityCat you see the carriages through this web, not over it.
      // The girder itself sits under the windows — it is the only solid thing
      // here and it never reaches the glass.
      ...nearTruss,
      solid(poly(`240,${gird(240)} 420,${gird(420)} 598,${gird(598)} 778,${gird(778)} 778,${round(gird(778) + 2.4)} 598,${round(gird(598) + 2.4)} 420,${round(gird(420) + 2.4)} 240,${round(gird(240) + 2.4)}`), BNE_NEAR),
      solid(poly(`240,${gird(240)} 420,${gird(420)} 598,${gird(598)} 778,${gird(778)} 778,${round(gird(778) + 0.7)} 598,${round(gird(598) + 0.7)} 420,${round(gird(420) + 0.7)} 240,${round(gird(240) + 0.7)}`), '#5e6873'),
    ],
  };
})();

// ─────────────────────────────────────────────────────────────────────────────
// ADELAIDE — the Oval across the Torrens, and the station on the approach.
//
// The line into Adelaide Railway Station runs along the south bank with the
// Oval's roof arcs and its light towers on the other side of the river, the
// Riverbank footbridge's curve between the two, and the station's own stone
// frontage arriving last, in front of the train, which is what a terminus looks
// like from the platform side.
//
// Flat the whole way. There is no viaduct here and nothing to climb.
// ─────────────────────────────────────────────────────────────────────────────

const ADL_STONE = '#93897a';
const ADL_ROOF = '#57677a';

const adelaide = {
  wash: [],
  far: [
    // The Torrens, as a band ABOVE the near ground: in a flat side elevation,
    // further away is higher, and that is how this says "across the river".
    solid(box(0, 33.4, 800, 4.6), '#2b3d45'),
    solid(box(0, 33.4, 800, 0.6), '#3a5560'),
    // The Oval's stand, and the three roof arcs over it. Tall on purpose: the
    // roof is the building, and at y 26 it was behind the train.
    solid(box(340, 16, 256, 18), '#333f4b'),
    solid(box(340, 15, 256, 1.4), '#44515e'),
    ...repeat(3, 344, 84, (x) => solid(
      `M${x} 20Q${x + 41} -16 ${x + 82} 20L${x + 82} 27Q${x + 41} -7 ${x} 27Z`, ADL_ROOF,
    )),
    ...repeat(3, 344, 84, (x) => solid(
      `M${x + 4} 19Q${x + 41} -14 ${x + 78} 19L${x + 78} 21Q${x + 41} -11 ${x + 4} 21Z`, '#6b7b8d',
    )),
    // The light towers: thin, taller than anything else here. Five of the six
    // stand across the river; the sixth is the near one and it is in `near`.
    ...repeat(5, 386, 56, (x) => solid(box(x, -16, 1.6, 50), '#767e87')),
    ...repeat(5, 386, 56, (x) => solid(box(x - 3.6, -21, 8.8, 6), '#8b939b')),
    // Adelaide Railway Station: the stone frontage, BEHIND the train. It used
    // to stand in front, and 168 units of solid stone in front of a 168-unit
    // train is not a terminus, it is a curtain — once a lap the train simply
    // vanished. A station you see the train standing in is the picture.
    solid(box(584, 0, 168, 40), ADL_STONE),
    solid(box(580, -5, 176, 6), '#a49a8a'),
    solid(box(578, -8, 180, 3), '#9d9383'),
    solid(box(628, -18, 80, 10), '#a49a8a'),
    solid(poly('624,-18 668,-27 712,-18'), '#a49a8a'),
    solid(box(660, -16, 16, 6), '#7d7466'),
    solid(box(584, 7, 168, 1.8), '#7d7466'),
    ...repeat(7, 596, 23, (x) => solid(box(x, 11, 7, 29), '#7d7466')),
    ...repeat(7, 596, 23, (x) => solid(box(x + 1.4, 13, 4.2, 14), '#242a31')),
    solid(box(650, 22, 34, 18), '#6f6759'),
    solid(box(654, 24, 26, 16), '#242a31'),
    solid(box(584, 39, 168, 1.4), '#ffc978', 0.35),
  ],
  ground: [
    // The parkland the line runs through, and the path along it.
    solid(box(-40, 38, 880, 8), '#37472f'),
    solid(box(-40, 43.4, 880, 1.2), '#6b737d', 0.6),
    ...repeat(9, 30, 88, (x) => solid(box(x, 39.6, 1.2, 4.2), '#4a4038')),
    ...repeat(9, 30, 88, (x) => disc(x + 0.6, 38.8, 3, '#3c5a3c')),
  ],
  near: [
    // The Riverbank footbridge, passing in front: one shallow curve on two slim
    // legs, with the hangers that hold its deck up.
    drawn('M120 40Q290 -2 460 40', '#aab1b8', 1.6),
    drawn('M120 40Q290 6 460 40', '#8f979f', 1),
    ...repeat(11, 148, 28, (x) => drawn(
      `M${x} ${round(40 - 21 * Math.sin((Math.PI * (x - 120)) / 340))}V40`, '#8f979f', 0.6,
    )),
    solid(box(118, 26, 3, 18), '#8f979f'),
    solid(box(459, 26, 3, 18), '#8f979f'),
    // The nearest Oval light tower, standing on THIS bank: a 2-unit mast past
    // the carriage roofs, a shade down from the five across the river, based
    // lower because the nearer of two identical things is. The whole tower is
    // narrower than one carriage window and the train runs straight past it.
    solid(box(326, -18, 2, 56), '#5c646d'),
    solid(box(321.4, -24, 11.2, 6.6), '#6f777f'),
    solid(box(320, -26, 14, 2), '#5c646d'),
    ...repeat(3, 322.6, 4, (x) => solid(box(x, -23, 2.4, 4.6), '#8a9299', 0.5)),
  ],
};

// ─────────────────────────────────────────────────────────────────────────────
// CANBERRA — Northbourne Avenue, with Black Mountain behind it.
//
// Canberra's whole network is one light rail line down one avenue, so the
// avenue is the world: the grass median the track sits in, the rows of trees
// along it, the carriageway either side, and the overhead the tram is actually
// drawing its power from. Telstra Tower stands on Black Mountain behind, and
// Parliament House's flagmast is small at the other end, which is the axis the
// city was laid out on.
// ─────────────────────────────────────────────────────────────────────────────

const canberra = {
  wash: [],
  far: [
    // Black Mountain, and the tower on it.
    solid('M-40 38Q140 -2 320 38Z', '#36463b'),
    solid('M40 38Q200 10 400 38Z', '#2f3e34'),
    solid(box(138, -6, 3.4, 30), '#8d949c'),
    solid(box(132, -2, 15.4, 5.4), '#a3aab1'),
    solid(box(134.6, 4.4, 10.2, 3), '#8d949c'),
    solid(box(139, -22, 1.6, 16), '#8d949c'),
    // Parliament House: the low pale building on its hill, and the flagmast.
    solid('M520 38Q620 26 760 38Z', '#42523a'),
    solid(box(566, 24, 152, 8), '#aeb2b6'),
    solid(box(566, 23, 152, 1.4), '#c4c8cc'),
    drawn('M626 24L642 -2M658 24L642 -2M634 24L642 -2M650 24L642 -2', '#9aa1aa', 0.9),
    solid(box(641.2, -12, 1.6, 12), '#9aa1aa'),
    solid(box(642.8, -12, 8, 3.6), '#7d858d'),
    // The far row of the tree median.
    ...repeat(18, 20, 44, (x) => solid(box(x, 30, 1.4, 8), '#4a4038')),
    ...repeat(18, 20, 44, (x) => disc(x + 0.7, 28.4, 4, '#35513c')),
  ],
  ground: [
    // The median, and the carriageway either side of it.
    solid(box(-40, 38, 880, 8), '#3b4a33'),
    solid(box(-40, 42.6, 880, 3.4), '#3b4149'),
    solid(box(-40, 42.2, 880, 0.5), '#6b737d', 0.5),
    ...repeat(21, -20, 42, (x) => solid(box(x, 44, 14, 0.7), '#e9eaec', 0.4)),
  ],
  near: [
    // The near row of trees, passing between you and the tram, and the poles
    // and wire that make it a tram at all.
    ...repeat(9, 48, 92, (x) => solid(box(x, -8, 1.4, 46), '#7d858d')),
    drawn('M-40 -6H840', '#7d858d', 0.7),
    ...repeat(9, 4, 92, (x) => solid(box(x, 36, 2, 10), '#413830')),
    ...repeat(9, 4, 92, (x) => disc(x + 1, 33.4, 5.4, '#2f4a36')),
    ...repeat(9, 4, 92, (x) => disc(x + 4, 30, 3.8, '#37543d')),
  ],
};

// ─────────────────────────────────────────────────────────────────────────────
// PERTH — across the Swan, past the stadium.
//
// The Armadale and Thornlie-Cockburn trains leave the city over the Swan River
// at Burswood, low on a concrete viaduct, with Perth Stadium's bronze bowl on
// the near bank and the city's towers across the water behind. The footbridge
// beside the line, Matagarup, is the landmark in front: two tall leaning arches
// with their stays fanned down to the deck, drawn as thin members so the
// carriages read straight through them.
//
// LEVEL, because the viaduct is. And no sky, like every other city: the
// towers stand against the app's own dark ground.
// ─────────────────────────────────────────────────────────────────────────────

const PER_STEEL = '#b9c0c6';
const PER_STEEL_NEAR = '#8f979f';

const perth = (() => {
  // One Matagarup arch: a leaning parabola from the deck, and the stays that
  // hang the deck from it, as one path each.
  const arch = (x0, x1, top, steel, sw, stay) => {
    const deck = 40;
    const mid = (x0 + x1) / 2;
    const c = 2 * top - deck;
    const at = (t) => ({
      x: round(((1 - t) ** 2) * x0 + 2 * t * (1 - t) * mid + (t ** 2) * x1),
      y: round(((1 - t) ** 2) * deck + 2 * t * (1 - t) * c + (t ** 2) * deck),
    });
    const stays = [0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8]
      .map(at)
      .map((p, i) => `M${p.x} ${p.y}L${round(x0 + ((x1 - x0) * (i + 1)) / 8)} ${deck}`)
      .join('');
    return [
      drawn(`M${x0} ${deck}Q${mid} ${c} ${x1} ${deck}`, steel, sw),
      drawn(stays, steel, stay, 0.8),
    ];
  };

  return {
    wash: [],
    far: [
      // The city across the river: flat slabs, every one tall enough to be
      // seen over the carriage roofs.
      ...[[30, -2, 14], [48, 6, 10], [62, -16, 15], [80, -6, 13], [96, -22, 12],
        [112, 0, 16], [132, -10, 13], [148, 4, 11], [164, -14, 14], [182, -4, 12],
        [198, 6, 10], [214, -8, 15], [232, 2, 12]]
        .map(([x, top, w], i) => solid(box(x, top, w, 30 - top), ['#3d4750', '#46525c', '#414b44'][i % 3])),
      // The far bank.
      solid(box(-40, 29, 880, 1.4), '#4e5a64'),
      // Perth Stadium: the bronze bowl and the light ring over it.
      solid(`M420 34Q426 6 520 4Q614 6 620 34Z`, '#7a5e42'),
      solid(`M432 34Q438 14 520 12Q602 14 608 34Z`, '#5f4a36'),
      ...repeat(9, 440, 20, (x) => solid(box(x, 14, 1.4, 20), '#8c6f50', 0.7)),
      drawn('M416 6Q520 -12 624 6', '#a2a9b0', 1.2),
      // The far arch of the footbridge, in the paler steel.
      ...arch(640, 780, -14, PER_STEEL, 1.6, 0.5),
    ],
    ground: [
      // The Swan, under a low concrete viaduct.
      solid(box(-40, 40.6, 880, 5.4), '#2b3d45'),
      solid(box(-40, 40.6, 880, 0.6), '#3a5560'),
      solid(box(-40, 38, 880, 2.6), '#5a6068'),
      ...repeat(10, 20, 80, (x) => solid(box(x, 40.6, 4, 5.4), '#4a5058')),
    ],
    near: [
      // The near arch: wider, springing lower, darker and thinner, so the
      // carriages show through it the way they do through Sydney's.
      ...arch(612, 800, -20, PER_STEEL_NEAR, 1.2, 0.4),
      // Lamp standards along the footpath, mast and head.
      ...repeat(5, 60, 110, (x) => solid(box(x, -4, 1.2, 44), '#5c646d')),
      ...repeat(5, 57.4, 110, (x) => solid(box(x, -6, 6.4, 2), '#6f777f')),
    ],
  };
})();

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Every world, keyed by city id.
 *
 * The order is the order the splash drives them through, and it is `city.js`'s
 * own order: Melbourne first, because that is the app's own city and the world
 * everybody who has ever opened this app has been looking at.
 */
export const BACKDROPS = {
  melbourne: {
    id: 'melbourne',
    name: 'A level crossing, and the sky rail that replaced one',
    // Drawn by hand in `TrainScene.vue`, unchanged. See the note at the top.
    wash: [],
    far: [],
    ground: [],
    near: [],
    lift: 'climb',
    liveries: ['melbourne-metro', 'vline', 'melbourne-tram'],
  },

  sydney: {
    id: 'sydney',
    name: 'The Sydney Harbour Bridge',
    ...sydney,
    lift: 'level',
    liveries: ['sydney-trains', 'sydney-metro', 'sydney-lightrail', 'nsw-xpt'],
  },

  brisbane: {
    id: 'brisbane',
    name: 'The Merivale bridge, and the Story Bridge behind it',
    ...brisbane,
    lift: 'rise',
    liveries: ['brisbane', 'gcoast-lightrail'],
  },

  adelaide: {
    id: 'adelaide',
    name: 'Adelaide Oval across the Torrens',
    ...adelaide,
    lift: 'level',
    liveries: ['adelaide', 'adelaide-tram'],
  },

  canberra: {
    id: 'canberra',
    name: 'Northbourne Avenue, and Black Mountain',
    ...canberra,
    lift: 'level',
    liveries: ['canberra'],
  },

  perth: {
    id: 'perth',
    name: 'The Swan at Burswood, and the stadium footbridge',
    ...perth,
    lift: 'level',
    liveries: ['perth', 'perth-regional'],
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// THE TRAM STREET: where a loader puts a tram.
//
// Not a city's world and NOT in `BACKDROPS`, so it is never in the splash's
// parade and no city "owns" it. A tram runs down a street, not through a sky
// rail station: a tree-lined boulevard behind (see BOULEVARD below), a strip of
// road, and the poles and wire that make it a tram. Nothing tall or wide enough
// to hide the vehicle at any point of the loop. Level, like the other flat
// worlds.
// ─────────────────────────────────────────────────────────────────────────────

// ── THE BOULEVARD, shared by both streets ───────────────────────────────────
//
// A Melbourne tree-lined avenue in the spirit of St Kilda Road, drawn in three
// depths (plus the ground) so the loader can give them their own speeds (see `sky` and `mid` below):
//
//   sky     a low, distant skyline and, in it, three recognisable silhouettes of
//           our own drawing: the Arts Centre's tapering lattice spire, the
//           Shrine's stepped roof and the Flinders Street dome. Slowest.
//   mid     a row of plane trees along the median strip, smaller and hazier.
//   far     the big plane trees along the footpath, behind the vehicle. Ground speed.
//
// COLOURS ARE TOKENS, not hexes: `var(--st-…)` set on `.scene` in
// `TrainScene.vue`, one value in the dark scheme and one in the light, so the
// same drawing works on a near-black page and on warm paper. Muted greens and
// blue-greys, all of them quieter than the livery, so the vehicle stays the hero.

/** A plane tree: a trunk, a back canopy in the dark green and the lit canopy in the light one. */
const planeTree = (x, { r = 11, cy = 2, trunkTo = 40, dark = 'var(--st-leaf-a)', light = 'var(--st-leaf-b)', op } = {}) => [
  solid(box(x - 1, cy + r * 0.5, 2, trunkTo - cy - r * 0.5), 'var(--st-trunk)', op),
  disc(x, cy, r, dark, op),
  disc(x - r * 0.55, cy + r * 0.18, r * 0.72, dark, op),
  disc(x + r * 0.58, cy + r * 0.12, r * 0.74, dark, op),
  disc(x - r * 0.12, cy - r * 0.28, r * 0.82, light, op),
  disc(x + r * 0.5, cy - r * 0.12, r * 0.5, light, op),
];

/**
 * The Arts Centre's spire, as our own drawing: a very slender plain needle
 * rising from a wide FLARED lattice skirt (a bell, much wider at the foot, like
 * a tutu), with a ring where the skirt meets the needle. The lattice runs down
 * the skirt only; the needle is a single fine line. Drawn in the skyline's own
 * haze colour so it sits far back.
 */
const spire = (x, baseY, h, w) => {
  const top = baseY - h;
  const ringY = top + h * 0.5;
  const skirtH = baseY - ringY;
  // Half-width of the skirt at y: nearly straight under the ring, flaring hard at the foot.
  const half = (y) => 1.2 + (w / 2 - 1.2) * ((y - ringY) / skirtH) ** 2.3;
  const rows = [0, 0.2, 0.4, 0.6, 0.8, 1].map((f) => ringY + skirtH * f);
  const left = rows.map((y) => `${round(x - half(y))} ${round(y)}`);
  const right = rows.map((y) => `${round(x + half(y))} ${round(y)}`);
  const braces = rows.slice(0, -1).map((y0, i) => {
    const y1 = rows[i + 1];
    return `M${round(x - half(y0))} ${round(y0)}L${round(x + half(y1))} ${round(y1)}M${round(x + half(y0))} ${round(y0)}L${round(x - half(y1))} ${round(y1)}`;
  }).join('');
  const rungs = rows.slice(1).map((y) => `M${round(x - half(y))} ${round(y)}H${round(x + half(y))}`).join('');
  const sky = 'var(--st-sky)';
  return [
    // The skirt's two flared sides, and a faint fill between them.
    solid(`M${left.join('L')}L${[...right].reverse().join('L')}Z`, sky, 0.25),
    drawn(`M${left.join('L')}`, sky, 0.6, 0.85),
    drawn(`M${right.join('L')}`, sky, 0.6, 0.85),
    drawn(braces, sky, 0.3, 0.8),
    drawn(rungs, sky, 0.35, 0.8),
    // The ring where the needle leaves the skirt, then the needle and its finial.
    solid(box(x - 2.6, ringY - 0.9, 5.2, 1.4), sky, 0.9),
    drawn(`M${x} ${top}V${round(ringY)}`, sky, 0.55, 0.9),
    disc(x, top - 0.6, 0.8, sky, 0.9),
    // The low building under it.
    solid(box(x - 15, baseY, 30, 22), sky, 0.45),
  ];
};

/** The Shrine's stepped roof over its columned base: a pyramid in four steps. */
const shrine = (x, baseY) => [
  solid(box(x - 20, baseY, 40, 18), 'var(--st-landmark)', 0.5),
  solid(box(x - 17, baseY - 3, 34, 3), 'var(--st-landmark)', 0.5),
  solid(box(x - 14, baseY - 7, 28, 4), 'var(--st-landmark)', 0.5),
  solid(box(x - 10, baseY - 11, 20, 4), 'var(--st-landmark)', 0.5),
  solid(box(x - 6, baseY - 15, 12, 4), 'var(--st-landmark)', 0.5),
  solid(box(x - 2.5, baseY - 19, 5, 4), 'var(--st-landmark)', 0.5),
  // The columns, as pale slots in the base.
  ...repeat(7, x - 15, 5, (cx) => solid(box(cx, baseY + 3, 1.6, 12), 'var(--st-sky)', 0.5)),
];

/** The Flinders Street dome: a low block, a dome on a drum, a small lantern. */
const dome = (x, baseY) => [
  solid(box(x - 24, baseY + 4, 48, 16), 'var(--st-landmark)', 0.45),
  solid(box(x - 8, baseY - 1, 16, 5), 'var(--st-landmark)', 0.5),
  solid(`M${x - 8} ${baseY - 1}A8 8 0 0 1 ${x + 8} ${baseY - 1}Z`, 'var(--st-landmark)', 0.55),
  solid(box(x - 1.2, baseY - 12, 2.4, 4), 'var(--st-landmark)', 0.55),
  solid(box(x - 0.3, baseY - 17, 0.6, 5), 'var(--st-landmark)', 0.55),
  ...repeat(2, x - 21, 38, (tx) => solid(box(tx, baseY - 2, 4, 8), 'var(--st-landmark)', 0.5)),
];

/** The distant skyline: a deterministic run of low blocks, `[x, width, height]`. */
const SKYLINE = [
  [0, 14, 12], [16, 9, 20], [27, 12, 15], [41, 8, 26], [51, 14, 17], [67, 10, 12], [79, 8, 23],
  [201, 12, 14], [215, 9, 22], [226, 14, 16], [242, 7, 28], [251, 12, 15], [265, 10, 19],
  [330, 14, 13], [346, 9, 24], [357, 12, 16], [371, 8, 20], [381, 14, 12],
  [470, 12, 15], [484, 9, 21], [495, 14, 14], [511, 8, 27], [521, 12, 17],
  [560, 14, 13], [576, 9, 22], [587, 12, 16], [601, 8, 19], [611, 14, 12],
  [690, 12, 14], [704, 9, 23], [715, 14, 16], [731, 8, 26], [741, 12, 15], [755, 10, 12], [768, 12, 18],
];

const BOULEVARD = {
  sky: [
    ...SKYLINE.map(([x, w, h]) => solid(box(x, 38 - h - 14, w, h + 14), 'var(--st-sky)', 0.45)),
    ...SKYLINE.filter((_, i) => i % 3 === 0).map(([x, w, h]) => solid(box(x + 1.5, 38 - h - 18, w - 3, 4), 'var(--st-sky)', 0.45)),
    ...spire(56, 8, 28, 17),
    ...shrine(430, 12),
    ...dome(640, 10),
  ],
  mid: [
    // The median strip's trees: smaller and hazier than the footpath's.
    ...repeat(12, 8, 68, (x, i) => planeTree(x + (i % 3) * 7, { r: 7.5, cy: 8, trunkTo: 39, op: 0.8 })).flat(),
  ],
  far: [
    // The footpath's big plane trees, behind the vehicle.
    ...repeat(8, 30, 100, (x, i) => planeTree(x + (i % 2) * 14, { r: 11.5, cy: 1 + (i % 3), trunkTo: 41 })).flat(),
  ],
};

const STREET_GROUND = [
  // A strip of the median's grass, then the footpath, then the road.
  solid(box(-40, 37.2, 880, 1), 'var(--st-grass)'),
  solid(box(-40, 38, 880, 2.6), 'var(--st-path)'),
  solid(box(-40, 40.6, 880, 5.4), 'var(--st-road)'),
  solid(box(-40, 41.2, 880, 0.5), 'var(--st-kerb)', 0.5),
  ...repeat(21, -20, 42, (x) => solid(box(x, 44, 14, 0.7), '#e9eaec', 0.35)),
];

export const TRAM_STREET = {
  id: 'tram-street',
  name: 'A tram boulevard: plane trees, the skyline, the Arts Centre spire and overhead wire',
  wash: [],
  ...BOULEVARD,
  ground: STREET_GROUND,
  near: [
    // Poles and the wire, thin enough to read straight through.
    ...repeat(9, 48, 92, (x) => solid(box(x, -8, 1.4, 46), 'var(--st-pole)')),
    drawn('M-40 -6H840', 'var(--st-pole)', 0.7),
  ],
  lift: 'level',
  liveries: [],
};

/**
 * THE BUS STREET: the same boulevard, with street lights where the tram has
 * its overhead wire. A route bus drives the same avenue of plane trees and the
 * same skyline; nothing reaches above the roof line that a bus does not.
 */
export const BUS_STREET = {
  ...TRAM_STREET,
  id: 'bus-street',
  name: 'A bus boulevard: plane trees, the skyline, the Arts Centre spire and street lights',
  near: [
    // Street lights: a thin pole and a short arm over the road.
    ...repeat(9, 48, 92, (x) => solid(box(x, 2, 1.2, 36), 'var(--st-pole)')),
    ...repeat(9, 48, 92, (x) => solid(box(x - 5, 1.4, 6.2, 1.2), 'var(--st-pole)')),
  ],
};

/** Every world id, in the order the splash reaches them. */
export const BACKDROP_IDS = Object.keys(BACKDROPS);

/** Melbourne, because that is the app that exists and the one people have. */
export const DEFAULT_BACKDROP = 'melbourne';

/**
 * Which world a livery belongs in, derived from the table rather than written
 * twice. A train is only ever drawn on its own city's scenery.
 */
export const LIVERY_BACKDROP = {
  ...Object.fromEntries(
    BACKDROP_IDS.flatMap((id) => BACKDROPS[id].liveries.map((livery) => [livery, id])),
  ),
  // A road vehicle has a street of its own, and is never in the splash parade:
  // a bus does not climb a sky rail viaduct.
  'melbourne-bus': BUS_STREET.id,
};

/**
 * The splash's running order: every train the app knows, GROUPED BY CITY.
 *
 * This is the whole reason the parade is not simply `LIVERY_IDS` any more. Thirteen
 * trains in livery-table order would be fine, but thirteen trains whose world
 * changed under half of them would have the harbour bridge turning into a
 * Melbourne boom gate and back again inside eight seconds. Grouped, the world
 * changes six times in a cycle instead of thirteen, and each change is a city
 * arriving rather than a glitch.
 */
export const PARADE = BACKDROP_IDS.flatMap((id) => BACKDROPS[id].liveries);

/**
 * The world to draw, from a city id OR a livery id.
 *
 * Both, on purpose, and it is not a convenience. The screens that wait — the
 * board, the train card, the planner — know which city they are in and ask
 * with that. The splash knows only which train is passing, because the parade
 * is a list of trains, and asking it to carry a parallel list of cities is how
 * the two lists come apart. One function, one table, and a livery that is not
 * filed under any city is a test failure rather than a blank screen.
 *
 * Never returns an id the table does not hold.
 */
export function backdropFor(id) {
  const key = String(id ?? '');
  if (Object.prototype.hasOwnProperty.call(BACKDROPS, key)) return key;
  if (key === TRAM_STREET.id || key === BUS_STREET.id) return key;
  return LIVERY_BACKDROP[key] ?? DEFAULT_BACKDROP;
}

/** A world by id, never undefined. */
export function backdrop(id) {
  const key = backdropFor(id);
  return BACKDROPS[key] ?? (key === BUS_STREET.id ? BUS_STREET : TRAM_STREET);
}

/**
 * How long the world takes to dissolve into the next one.
 *
 * Only the splash uses it, and only between passes. See the note in
 * `LoadingScreen.vue` for why the swap happens where it does.
 */
export const WORLD_FADE_MS = 420;
